import * as React from 'react'
import {
  Block,
  BlockChangeChildStatus,
  CommonBlocksCompoundIndexes,
  InstanceBlock,
  ListBlock,
  ListView,
  blockTypesRecord,
  GalleryView,
} from '@basehub/data/replicache/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  LoadMoreItemsButton,
  ShowOnFirstLoadMenu,
  useListTable,
} from '../../use-list-table'
import { Card } from './card'
import { Button } from '~/sotano-kit/primitives/button'
import { ArrowDownIcon, PlusIcon } from '@radix-ui/react-icons'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { invariant } from '@epic-web/invariant'
import { getId } from '@basehub/utils/id/index'
import { TreeManager } from '@basehub/utils/tree/index'
import { useSelectedView } from '../use-selected-view'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useGetTabPrefix, useTabsStore } from '../../../../tabs/store'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useEditorWidth } from '~/hooks/use-editor-width'
import { useEditorStore } from '../../../../editor-store'
import { useRepoStore } from '../../../../repo-store'
import { useEditorMode } from '../../../../editor-mode-provider'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { CreateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'

import {
  DroppableProvider,
  listBlockDndControllers,
  useListDndTreeNode,
} from '../dnd'
import { DroppableLeftZone, DroppableRightZone } from './droppable-zones'
import { ViewComponentProps } from '../../input'

export const ListInputGalleryView = ({
  block,
  path,
  listTable,
}: ViewComponentProps) => {
  const editorWidth = useEditorWidth()
  const justCreatedTableRowId = useEditorStore((s) => s.justCreatedTableRowId)
  const setJustCreatedTableRowId = useEditorStore(
    (s) => s.setJustCreatedTableRowId
  )
  const focus = useRepoStore((s) => s.focus)
  const isRenamingBlockId = useEditorStore((s) => s.isRenamingBlockId)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const setFocus = useRepoStore((s) => s.setFocus)
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const addTab = useTabsStore((s) => s.addTab)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const editorMode = useEditorMode()
  const { rows, columns, length, rowsShown, loadMore, allRowsShown } = listTable
  const [selectedView] = useSelectedView(block, { request: 'gallery' }) as [
    GalleryView,
    (view: ListView['layout']) => void,
  ]
  const readonly = editorMode === 'read'
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const sourceBlockId = idToSourceBlockId(block)
  const rootId = treeManager?.root.key
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  useListDndTreeNode({
    rootId: rootId ?? '',
    block,
    enabled: Boolean(rootId && block.id && !readonly),
    treeManager,
    undo: {
      manager: undoManager.current,
      focusOp: focusOpTable,
    },
  })

  const handleNewRow = React.useCallback(async () => {
    invariant(replicache, 'replicache should be defined')
    invariant(block.typeOptions !== null, 'block.typeOptions should be defined')
    const newCardId = getId()
    const params: CreateBlockParams = {
      data: {
        id: newCardId,
        title: '',
        type: blockTypesRecord.instance,
        typeOptions: {
          targetBlockId: block.typeOptions.targetBlockId,
        },
      },
      isoNow: new Date().toISOString(),
      position: { type: 'last' },
      path,
      idSeed: getId(),
      parentInstanceChildTarget: block.instanceChildTarget,
    }
    const { newBlock, undo } = await replicache.mutate.createBlock(params)

    const activeDocument = blockId
      ? treeManager?.getNode(blockId, {
          fallbackKey: 'sourceBlockId',
        })?.value
      : null

    setFocus({
      type: 'block:document-title',
      blockId: idToSourceBlockId(newBlock),
      temporaryBackLink: {
        title: activeDocument?.title ?? (block.title || 'Untitled'),
        id: idToSourceBlockId(activeDocument ?? block),
        edgeId: activeDocument ? idToSourceBlockId(block) : undefined,
      },
    })
    setIsRenamingBlockId(newBlock.id)
    setJustCreatedTableRowId(newBlock.id)

    if (undo) {
      const focusOp = {
        op: 'focus',
        args: {
          focus: {
            type: 'block:wrapper',
            blockId: sourceBlockId,
          },
          preferredTabKey: blockId,
        },
      } satisfies RepoUndoOps[number]
      undoManager.current?.push({
        redo: [focusOp, { op: 'createBlock', args: params }],
        undo: [
          focusOp,
          { op: 'blockBatchMutations', args: { mutations: undo } },
        ],
      })
    }
  }, [
    block,
    blockId,
    path,
    replicache,
    setFocus,
    setIsRenamingBlockId,
    setJustCreatedTableRowId,
    sourceBlockId,
    treeManager,
    undoManager,
  ])

  React.useEffect(() => {
    if (!treeManager || !justCreatedTableRowId) return

    const newRowIsChildOfList =
      treeManager.getNode(justCreatedTableRowId)?.parentKey === block.id
    if (!newRowIsChildOfList) return

    const newRowIsInRows = rows.some((row) => row.id === justCreatedTableRowId)
    if (newRowIsInRows) return

    addTab({
      key: justCreatedTableRowId,
      partiallyOpen: true,
    })
    setJustCreatedTableRowId(null)
  }, [
    addTab,
    block.id,
    setJustCreatedTableRowId,
    justCreatedTableRowId,
    rows,
    treeManager,
  ])

  let columnCount = 3
  if (editorWidth && editorWidth < 1024) {
    columnCount = 2
  } else if (editorWidth && editorWidth > 1440) {
    columnCount = 4
  }

  return (
    <>
      <div
        id={`${listBlockDndControllers.dndType}-${block.id}`}
        aria-hidden="true"
      />

      <div
        className="mt-1"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {rows.map((row, index) => {
          return (
            <div className="relative" key={idToSourceBlockId(row)}>
              {/* droppable zones */}
              <Card
                index={index}
                parentPath={path}
                parent={block}
                className="h-full"
                block={row}
                isRenaming={
                  (focus?.blockId === row.id && focus.type === 'block:name') ||
                  isRenamingBlockId === row.id
                }
                onFinishedRenaming={() => {
                  setJustCreatedTableRowId(null)
                  setIsRenamingBlockId(null)
                  setFocus(null)
                }}
                columns={columns}
                selectedView={selectedView}
                treeManager={treeManager}
              />

              <DroppableProvider>
                <DroppableRightZone
                  id={row.id}
                  parentBlock={block}
                  parentPath={path}
                  index={index}
                  onDropFilesCallback={(createdRows) => {
                    const createdRow = createdRows[0]
                    if (!createdRow) return
                    const buttonForTheNewBlock =
                      document.querySelector<HTMLButtonElement>(
                        `button[data-block-id="${createdRow.id}"]`
                      )
                    if (buttonForTheNewBlock) {
                      setTimeout(() => buttonForTheNewBlock.focus(), 1000)
                    }
                  }}
                />
                <DroppableLeftZone
                  parentBlock={block}
                  parentPath={path}
                  index={index}
                  id={row.id}
                  onDropFilesCallback={(createdRows) => {
                    const createdRow = createdRows[0]
                    if (!createdRow) return
                    const buttonForTheNewBlock =
                      document.querySelector<HTMLButtonElement>(
                        `button[data-block-id="${createdRow.id}"]`
                      )
                    if (buttonForTheNewBlock) {
                      setTimeout(() => buttonForTheNewBlock.focus(), 1000)
                    }
                  }}
                />
              </DroppableProvider>
            </div>
          )
        })}

        {!readonly && (
          <Button
            className="rounded-lg relative h-full min-h-18 font-medium text-xs text-dark-muted b hover:bg-dark-shade/50 cursor-default flex items-center justify-center border-dark-border-solid border-dashed border"
            unstyled
            focusStyled
            onClick={handleNewRow}
          >
            {!rows.length && (
              <DroppableProvider>
                <DroppableLeftZone
                  parentBlock={block}
                  parentPath={path}
                  index={0}
                  id={null}
                  onDropFilesCallback={(createdRows) => {
                    const createdRow = createdRows[0]
                    if (!createdRow) return
                    const buttonForTheNewBlock =
                      document.querySelector<HTMLButtonElement>(
                        `button[data-block-id="${createdRow.id}"]`
                      )
                    if (buttonForTheNewBlock) {
                      setTimeout(() => buttonForTheNewBlock.focus(), 1000)
                    }
                  }}
                />
              </DroppableProvider>
            )}
            <PlusIcon width={15} height={15} className="mr-1.5" />
            Add New
          </Button>
        )}
      </div>

      {!allRowsShown && (
        <>
          <hr className="border-dark-border-solid mt-4" />
          <LoadMoreItemsButton
            data-block-id={block.id}
            id={'load-more-rows-button-' + block.id}
            block={block}
            onClick={(e) => {
              if (e.target !== e.currentTarget) return
              loadMore()
            }}
          >
            <ArrowDownIcon width={16} height={16} className="mr-1.5" />
            Load more&nbsp;({length - rowsShown} remaining)&nbsp;&nbsp;
            {path && editorMode === 'write:all' && (
              <ShowOnFirstLoadMenu block={block} path={path} />
            )}{' '}
          </LoadMoreItemsButton>
        </>
      )}
    </>
  )
}

export const ListInputGalleryViewDiff = ({
  block,
  path,
  diff,
  treeManager,
  selectedView,
  condition,
}: {
  block: ListBlock
  path: string
  diff: Array<{ id: InstanceBlock['id']; status: BlockChangeChildStatus }>
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  selectedView: GalleryView
  fullView?: boolean
  condition: 'added' | 'removed'
}) => {
  const { rows, columns, length, rowsShown, loadMore, allRowsShown } =
    useListTable({
      block,
      treeManager,
      bypassFilterForRowIds: 'all',
    })
  const tabPrefix = useGetTabPrefix()

  return (
    <>
      <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4 mt-3">
        {rows.map((row, index) => {
          const match = (diff ?? []).find((diffItem) => {
            if (condition === 'removed') {
              return diffItem.id === row.id
            }

            return diffItem.id === idToSourceBlockId(row)
          })

          if (!match) {
            return (
              <Card
                key={idToSourceBlockId(row)}
                parentPath={path}
                parent={block}
                block={row}
                columns={columns}
                index={index}
                selectedView={selectedView}
                treeManager={treeManager}
                // disables context menu
                contextMenu={false}
                tabPrefix={tabPrefix}
              />
            )
          }

          let className = ''

          if (
            match.status === 'reordered-source' ||
            match.status === 'reordered'
          ) {
            className = '!bg-blue/10 text-dark-dim'
          }

          switch (match.status) {
            case 'created':
              className =
                'border-green-elevated hover:border-green-diff text-dark-dim'
              break
            case 'deleted':
              {
                if (condition === 'added') return undefined
                className =
                  'border-red-elevated hover:border-red-hover text-dark-dim opacity-30'
              }
              break
            case 'modified':
              className =
                '!bg-yellow/10 !border-yellow hover:!bg-yellow/15 text-dark-dim'
              break
          }

          return (
            <Card
              selectedView={selectedView}
              parentPath={path}
              key={idToSourceBlockId(row)}
              parent={block}
              block={row}
              columns={columns}
              index={index}
              className={className}
              treeManager={treeManager}
              // disables context menu
              contextMenu={false}
              tabPrefix={tabPrefix}
            />
          )
        })}
      </div>

      {!allRowsShown && (
        <>
          <hr className="border-dark-border-solid mt-4" />
          <LoadMoreItemsButton
            data-block-id={block.id}
            id={'load-more-rows-button-' + block.id}
            block={block}
            onClick={(e) => {
              if (e.target !== e.currentTarget) return
              loadMore()
            }}
          >
            <ArrowDownIcon width={16} height={16} className="mr-1.5" />
            Load more&nbsp;({length - rowsShown} remaining)&nbsp;&nbsp;
          </LoadMoreItemsButton>
        </>
      )}
    </>
  )
}
